import { useEffect } from 'react';

// Exported for testing purposes
export const _thumbnailCache = new Map<string, string>();

export function cacheKey(src: string, position: number, width: number): string {
  return [src, position, width].join();
}

/**
 * Retrieve thumbnail data URLs that might have been previously cached
 */
export function useGetFromThumbnailCache(
  src: string,
  position: number,
  width: number
): string | undefined {
  return _thumbnailCache.get(cacheKey(src, position, width));
}

/**
 * Adds the loaded thumbnail to the cache after video seeks
 */
export function useAddToThumbnailCache(
  video: HTMLVideoElement | null,
  src: string,
  position: number,
  width: number,
  height: number
): void {
  useEffect(() => {
    if (!(video instanceof HTMLVideoElement)) {
      return;
    }

    let timeout: NodeJS.Timeout;

    function onSeeked() {
      // Wait a moment after the seeked event
      // This works around Safari not completing the seek when the event fires
      // Also reduces resource usage when all thumbnails are rendering at once
      timeout = setTimeout(() => {
        const key = cacheKey(src, position, width);
        if (_thumbnailCache.has(key)) return;

        try {
          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;
          const context = canvas.getContext('2d');
          if (context) {
            context.drawImage(video, 0, 0, width, height);
            _thumbnailCache.set(key, canvas.toDataURL('image/jpeg', 0.8));
          }
        } catch (e) {
          console.error('Thumbnail caching failed:', e);
        }
      }, 3000);
    }

    video?.addEventListener('seeked', onSeeked);

    return () => {
      clearTimeout(timeout);
      video?.removeEventListener('seeked', onSeeked);
    };
  }, [video, src, position, width, height]);
}
